import { WordPress } from "../../../../wordpress/wordpress_api";
import { CMSState } from "../../../../cms/blocks/types.gen";
import { PublicSite_CustomPageData } from "../../../application.mod";
import { IOWidget, Action, mk_widget } from "widgets-for-react";
import { PublicSiteState } from "../../../../public_site.main.mod";
import { InView } from 'react-intersection-observer';
import React from "react";
import { blockInviewHandler } from "../../../utils/inview_utils";
import { getImageAttribute } from "../../../utils/extra_images.mod";

export type ImageStackBlockItem = {
    image: WordPress.MediaResponse
    caption: string
}

export type ImageStackAttributes = {
  blockName: 'vidda/image-stack'
  title: string
  intro: string
  items: Array<ImageStackBlockItem>
  block_index: number
}

export interface ImageStackInterface {
    blockName: 'vidda/image-stack'
    attrs: ImageStackAttributes
}

export type ImageStackBlockProps<extra_images> = ImageStackAttributes

export type ImageStackBlockStyle = Partial <{
    block_classname: string
    inner_classname: string
    header_classname: string
    title_classname: string
    content_classname: string
    intro_classname: string
    visual_classname: string
}>

export function ImageStackBlock<customBlockData, extra_images>(
  props: ImageStackBlockProps<extra_images>,
  options?: { style: ImageStackBlockStyle; key?: string }
): (s: CMSState<customBlockData, extra_images, PublicSite_CustomPageData>) => IOWidget<PublicSiteState, Action<CMSState<customBlockData, extra_images, PublicSite_CustomPageData>>> {
  return (s: CMSState<customBlockData, extra_images, PublicSite_CustomPageData>) => (public_state: PublicSiteState) => {
    return mk_widget({
      run: _ => (
        <section className="block block--image-stack">
          <div className="block__inner container--l grid--block">
            {(props.title || props.intro) && (
              <InView
                as="header"
                onChange={inView =>
                  blockInviewHandler('stack', inView, `stack-block-${props.block_index}-header`, 'block__header--inview')
                }
                triggerOnce={true}
                threshold={0.3}
                id={`stack-block-${props.block_index}-header`}
                key={`stack-block-${props.block_index}-header`}
                className={'block__header'}
              >
                {props.title && <h2 className="block__title typo--3xl">{props.title}</h2>}
                {props.intro && (
                  <p className="block__intro typo--xs" dangerouslySetInnerHTML={{ __html: props.intro }} />
                )}
              </InView>
            )}

            <div className="block__content">
              <ImageStack>
                {props.items.map((item, index) => (
                  <InView
                    as="article"
                    onChange={inView =>
                      blockInviewHandler(
                        'stack',
                        inView,
                        `stack-block-${props.block_index}-item-${index}`,
                        'imagestack__item--inview'
                      )
                    }
                    triggerOnce={true}
                    threshold={0.25}
                    id={`stack-block-${props.block_index}-item-${index}`}
                    className={'imagestack__item'}
                    key={`block-${index}`}
                  >
                    <figure className="imagestack__visual">
                      <img
                        src={getImageAttribute(item.image, 'stack', 'source_url')}
                        width={getImageAttribute(item.image, 'stack', 'width')}
                        height={getImageAttribute(item.image, 'stack', 'height')}
                        className="imagestack__image"
                        alt={item.image && item.image.alt_text ? item.image.alt_text : ''}
                      />
                      {item.caption && (
                        <figcaption className="imagestack__caption typo--2xs">{item.caption}</figcaption>
                      )}
                    </figure>
                  </InView>
                ))}
              </ImageStack>
            </div>
          </div>
        </section>
      )
    })
  }}

type ImageStackState = {
  height: number | string
}
class ImageStack extends React.Component<{}, ImageStackState> {
  private raf: number
  private ref: React.RefObject<HTMLDivElement> | null = null
  private windowWidth: number = 0

  constructor(props) {
    super(props)

    this.state = {
      height: 5000
    }

    this.ref = React.createRef<HTMLDivElement>()

    this.updateHeight = this.updateHeight.bind(this)
    this.raf = window.requestAnimationFrame(this.updateHeight)
  }

  componentWillUnmount() {
    window.cancelAnimationFrame(this.raf)
  }

  updateHeight(t: number) {
    this.raf = window.requestAnimationFrame(this.updateHeight)

    if (this.ref.current) {
      this.windowWidth = window.outerWidth
      if (this.windowWidth < 768) {
        this.setState({ height: null })
      } else {
        const cH: number = this.getChildrenHeight()
        if (cH !== this.state.height) {
          this.setState({ height: cH })
        }
      }
    }
  }

  getChildrenHeight() : number {
    let heightOne: number = 0
    let heightTwo: number = 0
    if (this.ref.current) {
      Array.from(this.ref.current.childNodes).forEach((child: HTMLDivElement, index: number) => {
        const style: CSSStyleDeclaration= window.getComputedStyle(child)
        let childHeight: number = child.clientHeight + 5 + (style.marginBottom ? parseInt(style.marginBottom) : 0)

        index % 2 == 0 ?
          heightOne += childHeight
        : heightTwo += childHeight
      })
    }

    return Math.max(heightOne, heightTwo)
  }

  render() {
    return (
      <div  className="imagestack" ref={this.ref} style={{ height: this.state.height }}>
        {this.props.children}
      </div>
    )
  }
}
